import { NonQuotaTargeting, rangeUnitCode } from '../../helpers';
import { IndividualQuota, Quota, QuotaCriteria } from '../../models';
import { ConditionLabels } from '../../survey-qualifications/selected-conditions';
import { BasePayloadMapper } from '@ps/utils';
import { Result } from '@sapphire/result';
import {
  IndividualQuotaDto,
  QuotaCriterionDto,
  QuotaPayloadDto,
  QuotaRangeSetDto,
} from '../../dtos/survey-quota.dto';
import {
  ChildAgeGenderCriterionFailure,
  isChildAgeGenderQuota,
  toChildAgeGenderCriterion,
} from './child-age-gender/child-age-gender-quota.mapper';

// A quota can only fail to map for the reasons its children criterion can — every other quota
// always produces a payload.
export type QuotaPayloadFailure = ChildAgeGenderCriterionFailure;

export class SurveyQuotaPayloadMapper extends BasePayloadMapper<
  Quota,
  Result<QuotaPayloadDto, QuotaPayloadFailure>
> {
  private readonly _criteriaMapper: SurveyQuotaCriteriaPayloadMapper;

  constructor(
    nonQuotaTargeting: NonQuotaTargeting,
    // The 232 qualification's own condition-code -> name table (see
    // childAgeGenderConditionNameLookup) and the survey's shared children age unit (see
    // childAgeUnitFor) — both resolved once by the caller from the full quota/qualification set,
    // since a single quota has no way to derive either on its own. Optional so a caller with no
    // use for children quotas (or an existing test) can still construct this with one argument.
    private readonly _childAgeGenderConditionNameByCode: ConditionLabels = new Map(),
    private readonly _childAgeUnit?: number,
  ) {
    super();
    this._criteriaMapper = new SurveyQuotaCriteriaPayloadMapper(
      nonQuotaTargeting,
    );
  }

  // Parse, don't validate: the payload is built inside `map`, so it cannot be assembled on the
  // failure path at all. Errs only for a children row that has nothing valid to send (see
  // _criteria) — every other quota always maps.
  public toPayload(quota: Quota): Result<QuotaPayloadDto, QuotaPayloadFailure> {
    return this._criteria(quota).map((criteria) => ({
      buyer_quota_id: quota.buyerQuotaId,
      required_count: quota.requiredCount,
      flexible: quota.flexible,
      group_key: quota.groupKey,
      criteria,
      maximum: quota.maximum,
      minimum: quota.minimum,
      percentage: quota.percentage,
      quota_category: quota.quotaCategory,
      individual_quotas: quota.individualQuotas.map((individualQuota) =>
        this._individualQuota(individualQuota),
      ),
      ...(quota.quotaTitle && { quota_title: quota.quotaTitle }),
    }));
  }

  private _individualQuota(
    individualQuota: IndividualQuota,
  ): IndividualQuotaDto {
    return {
      allocation: { ...individualQuota.allocation },
      criteria: this._criteriaMapper.toPayload(individualQuota.criteria),
    };
  }

  // A children (218) quota row is converted to its real 232 wire representation here, at the
  // quota level — only here is the full Quota (criteria + groupKey) available for
  // isChildAgeGenderQuota's own criteria[0].qualificationName discriminant to key off;
  // SurveyQuotaCriteriaPayloadMapper only ever sees bare QuotaCriteria, with no quota context to
  // make that call. A children row that has nothing valid to send errs with the reason rather
  // than shipping a broken criterion, and the caller decides what to do with it — see
  // toChildAgeGenderCriterion's own contract. Every other quota's criteria map exactly as before.
  private _criteria(
    quota: Quota,
  ): Result<QuotaCriterionDto[], QuotaPayloadFailure> {
    if (!isChildAgeGenderQuota(quota)) {
      return Result.ok(this._criteriaMapper.toPayload(quota.criteria));
    }
    return toChildAgeGenderCriterion(
      quota,
      this._childAgeGenderConditionNameByCode,
      this._childAgeUnit,
    ).map(({ criterion, childAgeUnit }) => [
      {
        ...this._criteriaMapper.toPayload([criterion])[0],
        child_age_unit: childAgeUnit,
      },
    ]);
  }
}

// A children (218) quota's criterion arrives here already converted to its real 232 wire shape —
// SurveyQuotaPayloadMapper does that conversion (via ./child-age-gender/child-age-gender-quota.mapper's
// toChildAgeGenderCriterion) before calling this mapper, since only there is the full Quota (the discriminant that
// conversion needs) available; this mapper only ever sees bare QuotaCriteria. So
// qualification_code is a plain passthrough here — never a place that itself decides 218 vs 232.
export class SurveyQuotaCriteriaPayloadMapper extends BasePayloadMapper<
  QuotaCriteria[],
  QuotaCriterionDto[]
> {
  constructor(private readonly _targeting: NonQuotaTargeting) {
    super();
  }

  public toPayload(data: QuotaCriteria[]): QuotaCriterionDto[] {
    return data.map((criterion) => ({
      qualification_code: criterion.qualificationCode,
      qualification_name: criterion.qualificationName,
      condition_codes: criterion.conditionCodes,
      condition_names: criterion.conditionNames,
      range_sets: this._rangeSets(criterion),
      original_maximum: criterion.originalMaximum,
      original_minimum: criterion.originalMinimum,
      original_percentage: criterion.originalPercentage,
      original_required_count: criterion.originalRequiredCount,
      original_number: criterion.originalNumber,
      original_flex: criterion.originalFlex,
      non_quota_targeted: this._targeting.flagForQuotaRow(
        criterion.qualificationCode,
      ),
    }));
  }

  // criterion.rangeSets is kept in sync with rangeMin/rangeMax (and the correct unit) in state
  // itself the moment either changes (see patchFirstCriterionRange/rangeSetsFor) — it's the
  // already-correct source of truth, so it's used verbatim whenever present. Only a criterion
  // that somehow never went through that sync (rangeSets absent/empty) falls back to rebuilding
  // from rangeMin/rangeMax here, where an unnamed unit resolves to the 0 placeholder rather than
  // a guessed one (see rangeUnitCode).
  private _rangeSets(criterion: QuotaCriteria): QuotaRangeSetDto[] {
    if (criterion.rangeSets?.length) {
      return criterion.rangeSets;
    }
    if (
      typeof criterion.rangeMin !== 'number' ||
      typeof criterion.rangeMax !== 'number'
    ) {
      return [];
    }
    return [
      {
        from: criterion.rangeMin,
        to: criterion.rangeMax,
        units: rangeUnitCode(criterion.rangeUnit),
      },
    ];
  }
}
